/**
 * Assign-crew picker (SCH.6).
 *
 * The explicit, discoverable way for a supervisor to put crew on a task: a
 * popover listing the roster with each member's load + conflict for the task's
 * scheduled day, multi-select, and a lead marker. Saving reconciles the whole
 * crew set (PUT /tasks/:id/assignments) and notifies the crew. Schedule-first —
 * disabled until the task has a date (the day's load can't be shown otherwise).
 */
import { useMemo, useState } from 'react';
import { ArrowLeftRight, Loader2, Star, TriangleAlert, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { CrewRoleCounts } from '@vari-crew/schemas';
import { useDispatchDay } from '@/api/dispatch';
import { useSetTaskAssignments } from '@/api/tasks';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { isoToDay } from '@/lib/calendarGrid';
import { formatRoleDemand } from '@/lib/crewRoles';

export interface AssignCrewButtonProps {
  task: {
    id: string;
    title: string;
    scheduled_start: string | null;
    /** When provided, crew already booked on another task overlapping this
     *  window are blocked from selection (no double-booking on the slot). */
    scheduled_end?: string | null;
    crew_size_needed?: number;
    /** Planned per-role crew mix (from the studio) — shown as the target demand. */
    crew_roles_needed?: CrewRoleCounts | null;
  };
}

/** Two [start,end) windows overlap? */
function windowsOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return new Date(aStart) < new Date(bEnd) && new Date(bStart) < new Date(aEnd);
}
function fmtClock(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function AssignCrewButton({ task }: AssignCrewButtonProps) {
  const [open, setOpen] = useState(false);
  const day = isoToDay(task.scheduled_start);

  return (
    <>
      <button
        type="button"
        disabled={!day}
        title={day ? 'Assign crew' : 'Schedule the task first to assign crew'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-accent shrink-0 disabled:opacity-40 disabled:hover:text-ink-faint"
      >
        <Users className="w-3.5 h-3.5" />
        Assign crew
      </button>
      {open && day && (
        <AssignCrewDialog task={task} day={day} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/** Mounted only while open, so the dispatch-day fetch happens on demand. */
export function AssignCrewDialog({
  task,
  day,
  onClose,
}: {
  task: AssignCrewButtonProps['task'];
  day: string;
  onClose: () => void;
}) {
  const dispatch = useDispatchDay(day);
  const set = useSetTaskAssignments(task.id);
  const rows = dispatch.data?.crew_rows ?? [];

  // Who's on this task right now, per the day's assignments.
  const current = useMemo(() => {
    const sel = new Set<string>();
    let lead: string | null = null;
    for (const row of rows) {
      const a = row.assignments.find((x) => x.task_id === task.id);
      if (a) {
        sel.add(row.crew_member.id);
        if (a.is_lead) lead = row.crew_member.id;
      }
    }
    return { sel, lead };
  }, [rows, task.id]);

  // Local edits override the loaded state once the user touches anything.
  const [override, setOverride] = useState<{ sel: Set<string>; lead: string | null } | null>(
    null,
  );
  const view = override ?? current;

  // Crew the supervisor has explicitly chosen to *move here* off a conflicting
  // task. When the set is non-empty, save() sends reassign_conflicts so the
  // server vacates their clashing assignment instead of refusing the slot.
  const [reassigning, setReassigning] = useState<Set<string>>(new Set());

  function toggle(cid: string) {
    const base = override ?? current;
    const sel = new Set(base.sel);
    let lead = base.lead;
    if (sel.has(cid)) {
      sel.delete(cid);
      // Keep a lead designated whenever crew remain (VCS-401).
      if (lead === cid) lead = sel.size > 0 ? ([...sel][0] ?? null) : null;
      // Dropping them also cancels any pending reassign of that member.
      if (reassigning.has(cid)) {
        const next = new Set(reassigning);
        next.delete(cid);
        setReassigning(next);
      }
    } else {
      sel.add(cid);
      if (!lead) lead = cid; // first picked defaults to lead
    }
    setOverride({ sel, lead });
  }

  function makeLead(cid: string) {
    const base = override ?? current;
    if (!base.sel.has(cid)) return;
    setOverride({ sel: new Set(base.sel), lead: cid });
  }

  // Move a conflicted crew member onto this task: select them and flag the move
  // so save() asks the server to vacate their clashing assignment.
  function reassign(cid: string) {
    const base = override ?? current;
    const sel = new Set(base.sel);
    sel.add(cid);
    const lead = base.lead ?? cid;
    setOverride({ sel, lead });
    setReassigning((prev) => new Set(prev).add(cid));
  }

  function save() {
    const moves = [...view.sel].filter((cid) => reassigning.has(cid)).length;
    set.mutate(
      {
        assignments: [...view.sel].map((cid) => ({
          crew_member_id: cid,
          is_lead: cid === view.lead,
        })),
        ...(moves > 0 ? { reassign_conflicts: true } : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            view.sel.size === 0
              ? 'Crew cleared'
              : moves > 0
                ? `Crew assigned (${view.sel.size}) · ${moves} reassigned`
                : `Crew assigned (${view.sel.size})`,
          );
          onClose();
        },
        onError: (err) => toast.error(`Assign failed: ${err.message}`),
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      busy={set.isPending}
      title={
        <>
          <Users className="w-4 h-4" />
          Assign crew
        </>
      }
    >
        <div className="p-4 space-y-3">
          <div>
            <div className="font-medium text-sm truncate">{task.title}</div>
            <div className="text-[11px] text-ink-faint">
              {view.sel.size} of {task.crew_size_needed ?? 1} needed · load shown for{' '}
              {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </div>
            {formatRoleDemand(task.crew_roles_needed) && (
              <div className="text-[11px] text-ink-dim">
                Plan: {formatRoleDemand(task.crew_roles_needed)}
              </div>
            )}
          </div>

          {dispatch.isLoading ? (
            <div className="text-xs text-ink-faint py-4 text-center">Loading crew…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-ink-faint py-4 text-center">No active crew.</div>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-auto">
              {rows.map((row) => {
                const cm = row.crew_member;
                const selected = view.sel.has(cm.id);
                const isLead = view.lead === cm.id;
                // Already on another task overlapping this slot? Block selecting
                // them (unless they're already on this task, so you can unassign).
                const clash =
                  task.scheduled_start && task.scheduled_end
                    ? row.assignments.find(
                        (a) =>
                          a.task_id !== task.id &&
                          a.scheduled_start &&
                          a.scheduled_end &&
                          windowsOverlap(
                            a.scheduled_start,
                            a.scheduled_end,
                            task.scheduled_start!,
                            task.scheduled_end!,
                          ),
                      )
                    : undefined;
                const blocked = !!clash && !selected;
                // Selected via the Reassign action — they're being moved here off
                // the clashing task (which still shows in the day's data until save).
                const movingHere = selected && reassigning.has(cm.id) && !!clash;
                const clashTitle = clash
                  ? dispatch.data?.tasks_by_id?.[clash.task_id]?.title
                  : undefined;
                const pct =
                  row.capacity_hours > 0
                    ? Math.round((row.assigned_hours / row.capacity_hours) * 100)
                    : 0;
                return (
                  <li
                    key={cm.id}
                    className={cn(
                      'flex items-center gap-2 rounded-md border px-2 py-1.5',
                      selected ? 'border-accent bg-accent/5' : 'border-line',
                      blocked && 'opacity-60',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={blocked}
                      onChange={() => toggle(cm.id)}
                      aria-label={`Assign ${cm.user.display_name}`}
                      title={blocked ? 'Already booked during this time slot' : undefined}
                      className="shrink-0 disabled:cursor-not-allowed"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">
                        {cm.user.display_name}
                        <span className="text-ink-faint"> · {cm.crew_role}</span>
                      </div>
                      <div className="text-[11px] flex items-center gap-1.5">
                        <span
                          className={cn(
                            pct > 100
                              ? 'text-status-blocked-fg'
                              : pct >= 80
                                ? 'text-amber-700'
                                : 'text-ink-faint',
                          )}
                        >
                          {row.assigned_hours}h / {row.capacity_hours}h ({pct}%)
                        </span>
                        {movingHere && clash ? (
                          <span className="inline-flex items-center gap-0.5 text-accent">
                            <ArrowLeftRight className="w-3 h-3" /> moving from{' '}
                            {clashTitle ?? 'another task'}
                          </span>
                        ) : blocked && clash ? (
                          <span className="inline-flex items-center gap-0.5 text-status-blocked-fg">
                            <TriangleAlert className="w-3 h-3" /> booked{' '}
                            {fmtClock(clash.scheduled_start!)}–{fmtClock(clash.scheduled_end!)}
                          </span>
                        ) : row.has_conflict ? (
                          <span className="inline-flex items-center gap-0.5 text-status-blocked-fg">
                            <TriangleAlert className="w-3 h-3" /> conflict
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {blocked ? (
                      <button
                        type="button"
                        onClick={() => reassign(cm.id)}
                        title="Move them here from their conflicting task"
                        className="shrink-0 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <ArrowLeftRight className="w-3 h-3" />
                        Reassign
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!selected}
                        onClick={() => makeLead(cm.id)}
                        title={isLead ? 'Lead' : 'Make lead'}
                        className={cn(
                          'shrink-0 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded',
                          isLead
                            ? 'text-amber-700 font-medium'
                            : 'text-ink-faint hover:text-ink disabled:opacity-30',
                        )}
                      >
                        <Star className={cn('w-3.5 h-3.5', isLead && 'fill-amber-400')} />
                        Lead
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {[...view.sel].some((cid) => reassigning.has(cid)) && (
            <p className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <ArrowLeftRight className="w-3 h-3 shrink-0" />
              Saving moves reassigned crew off their conflicting task.
            </p>
          )}
          {view.sel.size > 0 && !view.lead && (
            <p className="text-[11px] text-amber-700">
              Mark one crew member as the lead before saving.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={set.isPending}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={set.isPending || (view.sel.size > 0 && !view.lead)}
            >
              {set.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
    </Dialog>
  );
}
